import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { useLocation } from 'preact-iso';
import { AppShell } from '../components/AppShell';
import { PlantSubNav } from '../components/PlantSubNav';
import { Button } from '../components/ui/Button';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Table, type TableColumn } from '../components/ui/Table';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import { showConfirm } from '../components/Confirmer';
import { sessionSignal } from '../auth/session';
import type {
  LayoutDetailResponse,
  ListPlantsResponse,
  PlantSchemaResponse,
  PlantSummary,
  SaveLayoutRequest,
  SaveLayoutResponse,
  WidgetInstance,
} from '../api/types';
import { GridLayoutHost, addWidgetToHost, pushWidgets, removeWidgetFromHost } from '../layouts/gridLayoutHost';
import type { LayoutBody, LayoutMode, LayoutPage } from '../layouts/layoutHost';
import { getWidget } from '../layouts/widgetRegistry';
// Side-effect imports: register presentational + data-bound widget types
// with the widget registry so DashboardPage can render them without any
// special-casing. Order doesn't matter — registration is idempotent.
import '../layouts/presentationalWidgets';
import '../layouts/realDataWidgets';
import '../layouts/realDataWidgets.css';
import { render as renderPreact } from 'preact';
import { WidgetPicker } from '../components/WidgetPicker';
import { WidgetConfigEditor } from '../components/WidgetConfigEditor';

// Gridstack ships its own stylesheet — engine visuals we don't hand-design.
// Component-level colors are overridden in ./DashboardPage.css via CSS custom
// properties on `.hv-dashboard`.
import 'gridstack/dist/gridstack.min.css';
import './DashboardPage.css';

interface Props {
  plantId?: string;
}

const ENGINE_KIND = 'gridstack';

/** Fresh seed layout — one empty page, no tab bar (single-page dashboards
 *  hide the tabs). The WidgetPicker is the entry point for adding content. */
function defaultBody(): LayoutBody {
  const pageId = crypto.randomUUID();
  return {
    widgets: [],
    engineState: { items: [] },
    pages: [{ id: pageId, title: 'Übersicht', widgets: [], engineState: { items: [] } }],
    activePageId: pageId,
    tabOrientation: 'horizontal',
  };
}

/**
 * Normalise a saved layout body into the multi-page shape used in memory.
 * Legacy layouts without a <c>pages</c> field are wrapped into a single
 * implicit page so the rest of the code path stays uniform. New layouts
 * pass through unchanged; the top-level widgets/engineState are seeded
 * from the active page so a downstream reader that ignores <c>pages</c>
 * still gets a sensible snapshot.
 */
function normaliseBody(raw: LayoutBody): LayoutBody {
  if (raw.pages && raw.pages.length > 0) {
    const active = raw.pages.find((p) => p.id === raw.activePageId) ?? raw.pages[0];
    return {
      widgets: active.widgets,
      engineState: active.engineState,
      pages: raw.pages,
      activePageId: active.id,
      tabOrientation: raw.tabOrientation ?? 'horizontal',
    };
  }
  // Legacy body → one implicit page carrying the top-level content.
  const pageId = crypto.randomUUID();
  return {
    widgets: raw.widgets,
    engineState: raw.engineState,
    pages: [{ id: pageId, title: 'Übersicht', widgets: raw.widgets, engineState: raw.engineState }],
    activePageId: pageId,
    tabOrientation: 'horizontal',
  };
}

/**
 * Fold the currently-active page's live widgets + engineState (as reported
 * by the host) back into the pages array. Used before switching pages and
 * before persisting. Returns a fresh body with the pages up to date and
 * the top-level widgets/engineState mirroring the active page.
 */
function syncActivePageBack(body: LayoutBody): LayoutBody {
  const pages = body.pages ?? [];
  const activeId = body.activePageId ?? pages[0]?.id;
  const idx = pages.findIndex((p) => p.id === activeId);
  if (idx < 0) return body;
  const nextPages = pages.slice();
  nextPages[idx] = {
    ...nextPages[idx],
    widgets: body.widgets,
    engineState: body.engineState,
  };
  return { ...body, pages: nextPages };
}

export function DashboardPage({ plantId }: Props) {
  const [plantName, setPlantName] = useState<string | null>(null);
  const [plantSchema, setPlantSchema] = useState<PlantSchemaResponse | null>(null);
  const [layoutId, setLayoutId] = useState<string | null>(null);
  /** Fach, aus dem das geladene Layout kommt. Entscheidet mit, ob der
   *  Löschknopf erscheint: `Personal` liefert der Server nur für den
   *  angemeldeten Benutzer selbst, alles andere ist geteilt. */
  const [layoutKind, setLayoutKind] = useState<LayoutDetailResponse['kind'] | null>(null);
  /** Anzeigename des geladenen Layouts, für die Rückfrage vor dem Löschen.
   *  Ohne ihn stünde in der Rückfrage nur „dieses Dashboard“, und bei mehreren
   *  Anlagen nebeneinander weiß dann niemand, welches gemeint ist. */
  const [layoutName, setLayoutName] = useState<string | null>(null);
  const [version, setVersion] = useState<number>(1);
  const [body, setBody] = useState<LayoutBody | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  // Mode is a signal (not React state) so the LayoutHost can react without
  // re-mounting when the user toggles Edit/Run.
  const modeSignal = useMemo(() => signal<LayoutMode>('run'), []);
  const [mode, setMode] = useState<LayoutMode>('run');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Bumped whenever the layout host needs to be re-mounted (page switch,
   *  409-triggered reload). Different from `body === null` transitions
   *  because a page switch keeps body non-null. */
  const [mountKey, setMountKey] = useState(0);
  /** Which page is being renamed inline, or null. */
  const [renamingPageId, setRenamingPageId] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const unmountRef = useRef<(() => void) | null>(null);
  // Latest body — captured so save can PUT the current value without stale
  // closures. `body` state is only used to trigger re-renders of the widget
  // overlay; the host is the source of truth for engineState.
  const latestBodyRef = useRef<LayoutBody | null>(null);

  /**
   * Holt das für diese Anlage gültige Layout und setzt den Seitenzustand
   * darauf. Der Server entscheidet dabei, aus welchem Fach es kommt
   * (persönlich, Gruppenvorlage, Werkseinstellung); 404 heißt „in keinem
   * Fach etwas“ und wird zu einem frischen leeren Dashboard.
   *
   * Eigene Funktion, weil nach dem Löschen genau dasselbe passieren muss:
   * die Frage ist dann nicht „was war da“, sondern wieder „was gilt jetzt“,
   * und darunter liegt oft noch eine Vorlage. Wer stattdessen die gelöschte
   * Kennung nachladen oder einfach den Bildschirm leeren würde, zeigt dem
   * Bediener eine Leere, die es gar nicht gibt.
   */
  const loadLayout = async (signal?: AbortSignal) => {
    if (!plantId) return;
    try {
      const layout = await api.get<LayoutDetailResponse>(
        `/api/layouts/plant/${plantId}`,
        signal,
      );
      setLayoutId(layout.id);
      setLayoutKind(layout.kind);
      setLayoutName(layout.name);
      // Die Fassung gehört zum eigenen persönlichen Layout, und nur das kann
      // der Speicherweg überhaupt schreiben. Zeigt der Server eine Vorlage
      // (Gruppe oder Werk), gibt es noch kein persönliches: der nächste
      // Speichervorgang legt eines an und muss dafür mit 1 anfangen. Mit der
      // Fassung der Vorlage im Gepäck antwortete er 409, und zwar auf einen
      // Konflikt, den es nicht gibt. Sichtbar wird der Fall nach dem Löschen
      // des eigenen Layouts, weil dann die Vorlage darunter hervorkommt.
      setVersion(layout.kind === 'Personal' ? layout.version : 1);
      const parsed = normaliseBody(parseBody(layout.bodyJson));
      setBody(parsed);
      latestBodyRef.current = parsed;
      setDirty(false);
    } catch (err) {
      if (signal?.aborted) return;
      if (!(err instanceof ApiError) || err.status !== 404) {
        // Alles außer 404 ist ein echter Fehlschlag. Weitergeben, damit der
        // Aufrufer entscheiden kann: beim Erstaufbau wird er geschluckt, nach
        // einem Konflikt oder einem Löschvorgang gehört er in das Band.
        throw err;
      }
      const seed = defaultBody();
      setLayoutId(null);
      setLayoutKind(null);
      setLayoutName(null);
      setVersion(1);
      setBody(seed);
      latestBodyRef.current = seed;
      // Fresh personal layout — nothing to save yet unless the user edits.
      setDirty(false);
    }
  };

  // Load plant + layout in parallel. Layout is optional (404 → default body).
  useEffect(() => {
    if (!plantId) return;
    const ctrl = new AbortController();

    (async () => {
      try {
        const plant = await api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`, ctrl.signal);
        setPlantName(plant.name);
        setPlantSchema(plant);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof ApiError && err.status === 404) {
          setPlantName('Unknown plant');
        }
      }

      try {
        await loadLayout(ctrl.signal);
      } catch {
        // Erstaufbau: ein nicht erreichbarer Server zeigt sich schon über die
        // Anlagenabfrage oben. Hier nur schlucken, damit kein unbehandelter
        // Promise-Fehlschlag in der Konsole steht.
      }
    })();

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);

  // Sync mode signal with React state.
  useEffect(() => {
    modeSignal.value = mode;
  }, [mode, modeSignal]);

  // Mount the layout host. Re-mounts happen only on a mount-key bump
  // (fresh load, page switch, 409 reload) — Gridstack manages its own DOM
  // and we don't want a full teardown on every keystroke.
  //
  // The host's onChange feeds the emitted body back into React state, but
  // only for the ACTIVE page: the top-level widgets/engineState fields
  // mirror what the host reports and the pages[] array is updated on
  // demand (page switch, save).
  useEffect(() => {
    if (!containerRef.current || !body) return;

    const host = new GridLayoutHost();
    const unmount = host.mount(
      containerRef.current,
      body,
      (next) => {
        // `next` carries the host's fresh widgets + engineState for the
        // currently-mounted page. Preserve the multi-page envelope by
        // merging back into the ref's existing shape.
        const prev = latestBodyRef.current;
        const merged: LayoutBody = prev
          ? { ...prev, widgets: next.widgets, engineState: next.engineState }
          : next;
        latestBodyRef.current = merged;
        setDirty(true);
      },
      modeSignal,
    );
    unmountRef.current = unmount;
    pushWidgets(containerRef.current, body.widgets);

    return () => {
      unmount();
      unmountRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mountKey, body === null]);

  // Render widget bodies into the grid cells. Gridstack owns the outer chrome
  // (`.grid-stack-item` / `.grid-stack-item-content`); we mount our Preact
  // trees inside `.grid-stack-item-content` so styles and hover still work.
  //
  // In edit mode each cell wears an adorner overlay (currently: a delete
  // affordance in the top-right). The overlay is part of the Preact tree
  // rendered into `.grid-stack-item-content`, so Gridstack's drag/resize
  // handles keep working on the outer wrapper.
  useEffect(() => {
    if (!containerRef.current || !body) return;
    const gridEl = containerRef.current.querySelector('.grid-stack');
    if (!gridEl) return;

    const byId = new Map(body.widgets.map((w) => [w.id, w]));
    for (const item of Array.from(gridEl.querySelectorAll<HTMLElement>('.grid-stack-item'))) {
      const id = item.getAttribute('gs-id');
      if (!id) continue;
      const instance = byId.get(id);
      const content = item.querySelector('.grid-stack-item-content') as HTMLElement | null;
      if (!content) continue;

      const type = instance ? getWidget(instance.widgetTypeId) : undefined;
      const inner = !instance
        ? <div class="hv-widget-missing">Missing widget: {id}</div>
        : type
          ? type.render(instance, { plantId: plantId!, plantSchema })
          : (
            <div class="hv-widget-missing">
              Unknown widget type <code class="mono">{instance.widgetTypeId}</code>
            </div>
          );

      // Resolve "bare" chrome (no card border/padding on the grid cell).
      // Presentational widgets like text/heading/separator/spacer use it
      // unconditionally; image toggles via config.showFrame. We toggle a
      // class on the parent `.grid-stack-item-content` so a single CSS
      // rule strips the box treatment.
      const chromeResolved = !type || !type.chrome
        ? 'card'
        : typeof type.chrome === 'function'
          ? type.chrome(instance!)
          : type.chrome;
      content.classList.toggle('hv-widget-content--bare', chromeResolved === 'bare');

      renderPreact(
        <div class="hv-widget-frame">
          {inner}
          {mode === 'edit' && (
            <div class="hv-widget-adorners">
              <button
                type="button"
                class="hv-widget-adorner hv-widget-adorner--settings"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(id);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                aria-label="Widget konfigurieren"
                title="Widget konfigurieren"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                  {/* Wrench glyph — closed jaw top-left, diagonal handle to
                      bottom-right. Reads as tool at small sizes and matches
                      the stroke-based delete icon next to it. */}
                  <path
                    d="M9.2 1.6a3 3 0 0 0-3.6 4L2.2 9v2.6h2.6l3.4-3.4a3 3 0 0 0 4-3.6L10.7 6l-1.4-1.4 1.9-2z"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.2"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                class="hv-widget-adorner hv-widget-adorner--delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(id);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                aria-label="Widget entfernen"
                title="Widget entfernen"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                  {/* Trash-can glyph — lid, body, two vertical bars. */}
                  <path d="M2.5 3.5h9M5.5 3.5V2.5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1M3.5 3.5v8a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1v-8M6 5.5v5M8 5.5v5"
                    stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" fill="none" />
                </svg>
              </button>
            </div>
          )}
        </div>,
        content,
      );
    }
  });

  /** Remove one widget from the layout — used by the delete-adorner in
   *  edit mode. Mirrors onAddWidget: mutate the host, then trigger a React
   *  re-render so the widget effect re-walks the DOM. */
  const handleDelete = (id: string) => {
    const el = containerRef.current;
    if (!el) return;
    removeWidgetFromHost(el, id);
    const next = latestBodyRef.current;
    if (next) setBody({ ...next });
  };

  /**
   * Config-editor callback. The editor hands back a fresh WidgetInstance
   * (same id, mutated config). Merge it into the layout: update the
   * portable widget list on the host so `emit()` sees the new config on
   * the next change, then bump the body signal so the widget re-render
   * effect walks the DOM and passes the new instance to `type.render()`.
   */
  const handleSaveConfig = (next: WidgetInstance) => {
    const el = containerRef.current;
    const current = latestBodyRef.current;
    if (!el || !current) { setEditingId(null); return; }
    const merged: LayoutBody = {
      ...current,
      widgets: current.widgets.map((w) => (w.id === next.id ? next : w)),
    };
    // Keep the host in sync too so a subsequent grid mutation persists the
    // new config alongside the engineState.
    pushWidgets(el, merged.widgets);
    latestBodyRef.current = merged;
    setBody(merged);
    setDirty(true);
    setEditingId(null);
  };

  const editingInstance = editingId && body
    ? body.widgets.find((w) => w.id === editingId) ?? null
    : null;

  const onToggleMode = () => setMode((m) => (m === 'edit' ? 'run' : 'edit'));

  // ---- Multi-page handlers -----------------------------------------------
  // Every mutation goes through the same shape: read the latest body from
  // the ref (host is source-of-truth), fold the active page's live state
  // back into pages[], apply the mutation, sync back into React state and
  // bump the mount key when we actually swap the mounted page.

  const pages: LayoutPage[] = body?.pages ?? [];
  const activePageId = body?.activePageId ?? pages[0]?.id ?? '';

  const switchToPage = (targetId: string) => {
    const current = latestBodyRef.current;
    if (!current || !current.pages) return;
    if (targetId === current.activePageId) return;
    const withCurrentSaved = syncActivePageBack(current);
    const target = withCurrentSaved.pages!.find((p) => p.id === targetId);
    if (!target) return;
    const next: LayoutBody = {
      ...withCurrentSaved,
      widgets: target.widgets,
      engineState: target.engineState,
      activePageId: targetId,
    };
    latestBodyRef.current = next;
    setBody(next);
    setMountKey((k) => k + 1);
  };

  const addPage = () => {
    const current = latestBodyRef.current;
    if (!current) return;
    const withCurrentSaved = syncActivePageBack(current);
    const newPage: LayoutPage = {
      id: crypto.randomUUID(),
      title: `Seite ${(withCurrentSaved.pages?.length ?? 0) + 1}`,
      widgets: [],
      engineState: { items: [] },
    };
    const next: LayoutBody = {
      ...withCurrentSaved,
      pages: [...(withCurrentSaved.pages ?? []), newPage],
      activePageId: newPage.id,
      widgets: [],
      engineState: { items: [] },
    };
    latestBodyRef.current = next;
    setBody(next);
    setDirty(true);
    setMountKey((k) => k + 1);
  };

  const removePage = async (targetId: string) => {
    const current = latestBodyRef.current;
    if (!current || !current.pages) return;
    if (current.pages.length <= 1) return; // last page cannot be removed
    const ok = await showConfirm({
      title: 'Seite löschen?',
      message: 'Alle Widgets auf dieser Seite gehen verloren.',
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    const withCurrentSaved = syncActivePageBack(current);
    const nextPages = withCurrentSaved.pages!.filter((p) => p.id !== targetId);
    // If the target was active, jump to the first surviving page.
    const nextActive = targetId === withCurrentSaved.activePageId
      ? nextPages[0]
      : nextPages.find((p) => p.id === withCurrentSaved.activePageId) ?? nextPages[0];
    const next: LayoutBody = {
      ...withCurrentSaved,
      pages: nextPages,
      activePageId: nextActive.id,
      widgets: nextActive.widgets,
      engineState: nextActive.engineState,
    };
    latestBodyRef.current = next;
    setBody(next);
    setDirty(true);
    if (nextActive.id !== withCurrentSaved.activePageId) setMountKey((k) => k + 1);
  };

  const renamePage = (targetId: string, title: string) => {
    const current = latestBodyRef.current;
    if (!current || !current.pages) return;
    const nextPages = current.pages.map((p) =>
      p.id === targetId ? { ...p, title: title.trim() || 'Ohne Titel' } : p,
    );
    const next: LayoutBody = { ...current, pages: nextPages };
    latestBodyRef.current = next;
    setBody(next);
    setDirty(true);
    setRenamingPageId(null);
  };

  const toggleOrientation = () => {
    const current = latestBodyRef.current;
    if (!current) return;
    const next: LayoutBody = {
      ...current,
      tabOrientation: current.tabOrientation === 'vertical' ? 'horizontal' : 'vertical',
    };
    latestBodyRef.current = next;
    setBody(next);
    setDirty(true);
  };

  /**
   * Widget-picker callback. The host now owns the freshest engineState via
   * its onChange stream — we push the new instance through the host's
   * runtime API (adds a Gridstack cell + updates the portable list) and
   * then trigger a React re-render by shallow-cloning body so the render
   * effect walks the DOM again and paints the new cell's content.
   */
  const onAddWidget = (instance: WidgetInstance) => {
    const el = containerRef.current;
    const current = latestBodyRef.current;
    if (!el || !current) return;
    addWidgetToHost(el, instance);
    // Force the render-effect to fire by producing a new body reference.
    // The host's emit() already updated latestBodyRef via onChange, so the
    // widgets list is authoritative there. Read it back to stay in sync.
    const next = latestBodyRef.current ?? {
      widgets: [...current.widgets, instance],
      engineState: current.engineState,
    };
    setBody({ ...next });
    setPickerOpen(false);
  };

  const onSave = async () => {
    if (!plantId || saving) return;
    const current = latestBodyRef.current;
    if (!current) return;
    // Fold the mounted page's live widgets + engineState back into the
    // pages array so what we send matches what the operator sees.
    const bodyForPersist = syncActivePageBack(current);
    setSaving(true);
    setBanner(null);
    const payload: SaveLayoutRequest = {
      engineKind: ENGINE_KIND,
      bodyJson: JSON.stringify(bodyForPersist),
      version,
    };
    try {
      const res = await api.put<SaveLayoutResponse>(
        `/api/layouts/plant/${plantId}`,
        payload,
      );
      setLayoutId(res.id);
      setVersion(res.version);
      setDirty(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Reload authoritative version + body, then re-mount by nulling body.
        try {
          await loadLayout();
          setMountKey((k) => k + 1);
          setBanner('Layout wurde extern geändert — neu geladen.');
        } catch {
          setBanner('Konflikt beim Speichern — bitte Seite neu laden.');
        }
      } else {
        setBanner('Speichern fehlgeschlagen.');
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * Darf der angemeldete Benutzer das geladene Dashboard löschen?
   *
   * Die Regel spiegelt die des Servers, statt sie zu erfinden: ein
   * persönliches Layout gibt der Server ohnehin nur seinem Eigentümer heraus,
   * `kind === 'Personal'` heißt hier also „gehört mir“. Alles andere ist
   * geteilt und braucht dasselbe Recht wie das Bearbeiten von Layouts.
   *
   * Die Prüfung ist reine Anzeige, kein Riegel: der Riegel sitzt im Endpunkt.
   * Sie ist trotzdem nötig, weil ein Knopf, der verlässlich 403 liefert,
   * schlimmer ist als keiner.
   */
  const darfLoeschen = layoutId !== null
    && (layoutKind === 'Personal'
      || (sessionSignal.value.user?.permissions.includes('layout.edit') ?? false));

  const onDeleteDashboard = async () => {
    if (!plantId || layoutId === null || deleting) return;

    const bezeichnung = layoutName?.trim()
      ? layoutName.trim()
      : (plantName ?? 'Dashboard');
    const ok = await showConfirm({
      title: `Dashboard „${bezeichnung}“ löschen?`,
      message: layoutKind === 'Personal'
        ? 'Ihre eigene Ansicht dieser Anlage wird entfernt. Liegt darunter eine '
          + 'Vorlage, erscheint sie danach wieder.'
        : 'Die Ansicht wird für alle im Mandanten entfernt.',
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;

    setDeleting(true);
    setBanner(null);
    try {
      await api.delete<void>(`/api/layouts/${layoutId}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setBanner('Keine Berechtigung, dieses Dashboard zu löschen.');
        setDeleting(false);
        return;
      }
      if (!(err instanceof ApiError) || err.status !== 404) {
        setBanner('Löschen fehlgeschlagen.');
        setDeleting(false);
        return;
      }
      // 404: war schon weg (zweiter Klick, zweiter Tab). Kein Fehler, der
      // gewünschte Zustand ist erreicht. Weiter zum Neuladen.
    }

    try {
      // Nicht auf eine leere Seite gehen: neu fragen, was für diese Anlage
      // jetzt gilt. Das ist entweder die Vorlage, die unter dem gelöschten
      // Layout lag, oder ein frisches leeres Dashboard.
      await loadLayout();
      setMountKey((k) => k + 1);
      // Bearbeiten war für das alte Layout gemeint. Zurück in den Betrieb,
      // sonst steht der Bediener im Editiermodus vor etwas anderem als dem,
      // was er gerade bearbeitet hat.
      setMode('run');
    } catch {
      setBanner('Dashboard gelöscht, das Nachladen ist fehlgeschlagen. Bitte Seite neu laden.');
    } finally {
      setDeleting(false);
    }
  };

  if (!plantId) {
    return <DashboardPlantPicker />;
  }

  const title = plantName ? `Dashboard · ${plantName}` : 'Dashboard';

  return (
    <AppShell
      title={title}
      actions={(
        <div class="hv-dashboard__toolbar">
          <StatusBadge tone={mode === 'edit' ? 'warn' : 'neutral'}>
            {mode === 'edit' ? 'Edit mode' : 'Run mode'}
          </StatusBadge>
          <Button variant="ghost" size="sm" onClick={onToggleMode}>
            {mode === 'edit' ? 'Run' : 'Edit'}
          </Button>
          {mode === 'edit' && (
            <>
              <button
                class="hv-btn hv-btn--secondary hv-btn--sm"
                onClick={() => setPickerOpen(true)}
                type="button"
                title="Widget hinzufügen"
              >
                + Widget
              </button>
              {/* Native button — the shared Button component's Props type
                doesn't surface `disabled` (its HTMLAttributes<HTMLButtonElement>
                alias doesn't extend ButtonHTMLAttributes). Not worth widening
                the shared type from this slice. */}
              <button
                class={`hv-btn hv-btn--primary hv-btn--sm${(!dirty || saving) ? ' hv-btn--disabled' : ''}`}
                onClick={onSave}
                disabled={!dirty || saving}
                type="button"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              {/* Löschen steht bewusst am rechten Rand hinter dem Speichern
                  und nicht daneben: die beiden Knöpfe sind im Ablauf
                  gegensätzlich, und der Abstand kostet einen Fehlklick
                  weniger. Erscheint nur, wenn der Benutzer es auch darf. */}
              {darfLoeschen && (
                <button
                  class={`hv-btn hv-btn--danger hv-btn--sm hv-dashboard__delete${deleting ? ' hv-btn--disabled' : ''}`}
                  onClick={onDeleteDashboard}
                  disabled={deleting}
                  type="button"
                  title="Dashboard löschen"
                >
                  {deleting ? 'Löscht…' : 'Löschen'}
                </button>
              )}
            </>
          )}
          {layoutId === null && <StatusBadge tone="info">Not yet saved</StatusBadge>}
        </div>
      )}
    >
      {plantId && <PlantSubNav plantId={plantId} plantName={plantName ?? ''} />}
      {banner && <div class="hv-dashboard__banner">{banner}</div>}
      {body === null && <p class="muted">Loading layout…</p>}
      {body !== null && (
        <div class={`hv-dashboard-shell hv-dashboard-shell--${body.tabOrientation ?? 'horizontal'}`}>
          {(pages.length > 1 || mode === 'edit') && (
            <div
              class={`hv-tabs hv-tabs--${body.tabOrientation ?? 'horizontal'}${mode === 'edit' ? ' hv-tabs--edit' : ''}`}
              role="tablist"
            >
              {pages.map((p) => (
                <PageTab
                  key={p.id}
                  page={p}
                  active={p.id === activePageId}
                  editMode={mode === 'edit'}
                  renaming={renamingPageId === p.id}
                  canRemove={pages.length > 1}
                  onSelect={() => switchToPage(p.id)}
                  onRename={(title) => renamePage(p.id, title)}
                  onStartRename={() => setRenamingPageId(p.id)}
                  onCancelRename={() => setRenamingPageId(null)}
                  onRemove={() => removePage(p.id)}
                />
              ))}
              {mode === 'edit' && (
                <>
                  <button
                    type="button"
                    class="hv-tabs__add"
                    onClick={addPage}
                    title="Neue Seite hinzufügen"
                  >
                    + Seite
                  </button>
                  <button
                    type="button"
                    class="hv-tabs__orientation"
                    onClick={toggleOrientation}
                    title={body.tabOrientation === 'vertical' ? 'Tabs horizontal ausrichten' : 'Tabs vertikal ausrichten'}
                    aria-label="Tab-Orientierung umschalten"
                  >
                    {body.tabOrientation === 'vertical' ? '⇄' : '⇅'}
                  </button>
                </>
              )}
            </div>
          )}
          <div class="hv-dashboard-shell__body">
            {body.widgets.length === 0 && mode === 'run' && (
              <p class="muted hv-dashboard__empty">
                Kein Widget konfiguriert. Wechsle in den Edit-Modus und wähle „+ Widget".
              </p>
            )}
            <div class={`hv-dashboard hv-dashboard--${mode}`} ref={containerRef} />
          </div>
        </div>
      )}
      {pickerOpen && (
        <WidgetPicker
          onSelect={onAddWidget}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {editingInstance && plantId && (
        <WidgetConfigEditor
          instance={editingInstance}
          plantId={plantId}
          onSave={handleSaveConfig}
          onClose={() => setEditingId(null)}
        />
      )}
    </AppShell>
  );
}

/** Shown when the user hits `/dashboard` without a plantId — lists all
 *  visible plants and routes on click, so the top-nav entry never dead-ends. */
function DashboardPlantPicker() {
  const location = useLocation();
  const state = useAsync((sig) => api.get<ListPlantsResponse>('/api/plants', sig), []);

  const columns: TableColumn<PlantSummary>[] = [
    { key: 'name', header: 'Anlage', render: (p) => p.displayName },
    { key: 'addr', header: 'Ort', render: (p) => <span class="muted">{p.address ?? '—'}</span> },
  ];

  return (
    <AppShell title="Dashboard">
      <div class="stack-4">
        <p class="muted">Anlage wählen, deren Dashboard geöffnet werden soll:</p>
        {state.status === 'loading' && <p class="muted">Anlagen werden geladen…</p>}
        {state.status === 'error' && <p class="muted">Anlagen konnten nicht geladen werden.</p>}
        {state.status === 'ok' && (
          <Table
            columns={columns}
            rows={state.data.plants}
            rowKey={(p) => p.id}
            onRowClick={(p) => location.route(`/dashboard/${p.id}`)}
            emptyLabel="Keine Anlagen konfiguriert."
          />
        )}
      </div>
    </AppShell>
  );
}

/** Parse the server-side bodyJson blob. Failures fall back to an empty body
 *  so the UI still boots — the user can click Save to overwrite. Preserves
 *  the multi-page fields when present; normaliseBody() upgrades legacy
 *  single-page bodies into the pages[] shape. */
function parseBody(raw: string): LayoutBody {
  try {
    const parsed = JSON.parse(raw) as Partial<LayoutBody>;
    return {
      widgets: Array.isArray(parsed.widgets) ? parsed.widgets : [],
      engineState: parsed.engineState ?? { items: [] },
      pages: Array.isArray(parsed.pages) ? parsed.pages : undefined,
      activePageId: typeof parsed.activePageId === 'string' ? parsed.activePageId : undefined,
      tabOrientation: parsed.tabOrientation === 'vertical' ? 'vertical' : (parsed.tabOrientation === 'horizontal' ? 'horizontal' : undefined),
    };
  } catch {
    return { widgets: [], engineState: { items: [] } };
  }
}

/**
 * One tab in the multi-page bar. In run mode it's a plain button that
 * activates the page. In edit mode it grows a rename affordance (double-
 * click title) and a small × for removal (disabled when only one page
 * exists — the last page cannot be removed).
 */
function PageTab({
  page, active, editMode, renaming, canRemove,
  onSelect, onRename, onStartRename, onCancelRename, onRemove,
}: {
  page: LayoutPage;
  active: boolean;
  editMode: boolean;
  renaming: boolean;
  canRemove: boolean;
  onSelect(): void;
  onRename(title: string): void;
  onStartRename(): void;
  onCancelRename(): void;
  onRemove(): void;
}) {
  const [draftTitle, setDraftTitle] = useState(page.title);
  useEffect(() => { setDraftTitle(page.title); }, [page.title, renaming]);

  return (
    <div class={`hv-tab${active ? ' hv-tab--active' : ''}${editMode ? ' hv-tab--edit' : ''}`}
      role="tab"
      aria-selected={active}
    >
      {renaming ? (
        <input
          type="text"
          class="hv-tab__rename-input"
          value={draftTitle}
          autoFocus
          onInput={(e) => setDraftTitle((e.currentTarget as HTMLInputElement).value)}
          onBlur={() => onRename(draftTitle)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onRename(draftTitle);
            else if (e.key === 'Escape') onCancelRename();
          }}
        />
      ) : (
        <button
          type="button"
          class="hv-tab__label"
          onClick={onSelect}
          onDblClick={(e) => {
            if (!editMode) return;
            e.stopPropagation();
            onStartRename();
          }}
          title={editMode ? 'Klick: aktivieren · Doppelklick: umbenennen' : undefined}
        >
          {page.title || 'Ohne Titel'}
        </button>
      )}
      {editMode && !renaming && canRemove && (
        <button
          type="button"
          class="hv-tab__remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Seite entfernen"
          title="Seite entfernen"
        >
          ×
        </button>
      )}
    </div>
  );
}
